package one.healthium.uscreen.serialization

import kotlinx.serialization.KSerializer
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import one.healthium.uscreen.domain.models.StyledTextModel
import one.healthium.uscreen.domain.models.StyledTextModel.StyledSegment
import one.healthium.uscreen.domain.models.UScreenFontWeight

object StyledTextModelSerializer : KSerializer<StyledTextModel> {
    override val descriptor: SerialDescriptor =
        PrimitiveSerialDescriptor("StyledTextModel", PrimitiveKind.STRING)

    override fun deserialize(decoder: Decoder): StyledTextModel {
        val raw = decoder.decodeString()
        return parse(raw)
    }

    override fun serialize(encoder: Encoder, value: StyledTextModel) {
        encoder.encodeString(
            value.segments.joinToString("") { segment ->
                when (segment) {
                    is StyledSegment.Plain -> segment.text
                    is StyledSegment.Styled -> buildString {
                        append("<style")
                        val attrs = mutableListOf<String>()
                        segment.color?.let { attrs += "color=$it" }
                        segment.fontWeight?.let { attrs += "font-weight=${it.name.lowercase()}" }
                        segment.size?.let { attrs += "size=$it" }
                        if (segment.strike) attrs += "strike=true"
                        segment.bg?.let { attrs += "bg=$it" }
                        if (attrs.isNotEmpty()) {
                            append(" ")
                            append(attrs.joinToString("; "))
                        }
                        append(">")
                        append(segment.text)
                        append("</style>")
                    }
                }
            },
        )
    }

    private val STYLE_TAG_REGEX = Regex("""<style([^>]*)>(.*?)</style>""")

    fun parse(raw: String): StyledTextModel {
        val segments = mutableListOf<StyledSegment>()
        var lastIndex = 0

        STYLE_TAG_REGEX.findAll(raw).forEach { match ->
            if (match.range.first > lastIndex) {
                segments += StyledSegment.Plain(raw.substring(lastIndex, match.range.first))
            }
            val attrs = match.groupValues[1].trim()
            val text = match.groupValues[2]
            segments += parseStyledSegment(text, attrs)
            lastIndex = match.range.last + 1
        }

        if (lastIndex < raw.length) {
            segments += StyledSegment.Plain(raw.substring(lastIndex))
        }

        if (segments.isEmpty()) {
            segments += StyledSegment.Plain(raw)
        }

        return StyledTextModel(segments)
    }

    private fun parseStyledSegment(text: String, attrs: String): StyledSegment.Styled {
        if (attrs.isBlank()) return StyledSegment.Styled(text = text)
        var color: String? = null
        var fontWeight: UScreenFontWeight? = null
        var size: Int? = null
        var strike = false
        var bg: String? = null

        attrs.split(";").map { it.trim() }.filter { it.isNotEmpty() }.forEach { attr ->
            val parts = attr.split("=", limit = 2).map { it.trim() }
            if (parts.size == 2) {
                when (parts[0]) {
                    "color" -> color = parts[1]
                    "font-weight", "weight" -> fontWeight = parseFontWeight(parts[1])
                    "size" -> size = parts[1].toIntOrNull()
                    "strike" -> strike = parts[1] == "true"
                    "bg" -> bg = parts[1]
                }
            }
        }
        return StyledSegment.Styled(
            text = text,
            color = color,
            fontWeight = fontWeight,
            size = size,
            strike = strike,
            bg = bg,
        )
    }

    private fun parseFontWeight(value: String): UScreenFontWeight? = when (value.lowercase()) {
        "bold" -> UScreenFontWeight.Bold
        "semibold" -> UScreenFontWeight.SemiBold
        "normal" -> UScreenFontWeight.Normal
        else -> null
    }
}
